import { getStackTrace, log } from '@src/Log.init';
import { rootApi } from './root.api';
import fetchWithTimeout from './fetchAPI';

export async function getTenantLocations(token, tenantId) {
  const headers = new Headers();
  headers.append('token', token);
  const results = await fetchWithTimeout(
    `locationByTenant/${tenantId}`,
    {
      method: 'GET',
      headers: headers,
      cache: 'no-cache',
      redirect: 'follow',
      credentials: 'omit',
    },
    8000
  );
  const jsonResults = await results.json();

  return jsonResults;
}

export async function getWorkstationsByTenant(token, tenantId, locationId) {
  const headers = new Headers();
  headers.append('token', token);
  const results = await fetchWithTimeout(
    `workstationsByTenant/${tenantId}/${locationId}`,
    {
      method: 'GET',
      headers: headers,
      cache: 'no-cache',
      redirect: 'follow',
      credentials: 'omit',
    },
    3000
  );
  log.info({ message: `getWorkstationsByTenant: results = ${results}` });
  const jsonResults = await results.json();
  return jsonResults;
}

export const mapReferenceListToReferenceListResponse = (referenceList) => {
  const response = {
    name: referenceList.name,
    defaultType: referenceList.default_type,
    defaultValue: referenceList.default_value,
    list: referenceList.list,
  };
  return response;
};

export const mapLocationRequestToLocation = (requestBody) => {
  return {
    location_id: requestBody.key,
    location_name: requestBody.name,
    description: requestBody.description,
    aliases: requestBody.aliases,
    locale: requestBody.locale,
    type: requestBody.locationType,
    parameters: requestBody.parameters,
    templates: requestBody.templateName,
    status: requestBody.status,
    safe_id: requestBody.safeId,
    supported_languages: requestBody.languages,
    alternate_currencies: requestBody.currencies,
    country: requestBody.country,
    address_line_1: requestBody.addressLine1,
    address_line_2: requestBody.addressLine2,
    city: requestBody.city,
    state: requestBody.state,
    postal_code: requestBody.postalCode,
    telephone_number: requestBody.phone,
    card_payment_provider: requestBody.cardPaymentProcessor,
    card_processor_name: requestBody.cardProcessorName,
    gift_card_processor: requestBody.giftCardProcessor,
    gift_card_processor_name: requestBody.giftCardProcessorName,
    wallet_processor: requestBody.walletProcessor,
    wallet_processor_name: requestBody.walletProcessorName,
    poa_processor: requestBody.payOnAccountProcessor,
    poa_processor_name: requestBody.payOnAccountProcessorName,
    citcon_token: requestBody.citconToken,
    tax_id: requestBody.taxId,
  };
};

export const mapLocationToLocationRequest = (location) => {
  let referenceList = [];

  if (location.referenceLists) {
    referenceList = location.referenceLists.map((item) =>
      mapReferenceListToReferenceListResponse(item)
    );
  }

  return {
    id: location._id,
    key: location.location_id,
    name: location.location_name,
    description: location.description,
    aliases: location.aliases,
    locale: location.locale,
    locationType: location.type,
    parameterName: location.parameters,
    templateName: location.templates,
    status: location.status,
    safeId: location.safe_id,
    languages: location.supported_languages,
    currencies: location.alternate_currencies,
    country: location.country,
    addressLine1: location.address_line_1,
    addressLine2: location.address_line_2,
    city: location.city,
    state: location.state,
    postalCode: location.postal_code,
    phone: location.telephone_number,
    cardPaymentProcessor: location.card_payment_provider,
    cardProcessorName: location.card_processor_name,
    giftCardProcessor: location.gift_card_processor,
    giftCardProcessorName: location.gift_card_processor_name,
    walletProcessor: location.wallet_processor,
    walletProcessorName: location.wallet_processor_name,
    payOnAccountProcessor: location.poa_processor,
    payOnAccountProcessorName: location.poa_processor_name,
    citconToken: location.citcon_token,
    taxId: location.tax_id,
    createdAt: location.created_at,
    updatedAt: location.updated_at,
    referenceList: referenceList,
  };
};

export const locationApi = rootApi.injectEndpoints({
  tagTypes: ['Location'],
  endpoints: (builder) => ({
    getLocations: builder.query({
      queryFn: async (arg = {}, { getState }, extraOptions, baseQuery) => {
        const state = getState();
        const tenantId =
          state.session.tenantID || state.tenant?.tenant?.tenant_id;

        if (!tenantId) {
          return {
            error: { status: 400, data: 'Tenant ID not found in state' },
          };
        }

        const { limit, offset, withTotal = true } = arg;
        const queryParams = new URLSearchParams();
        if (limit) queryParams.append('limit', limit);
        if (offset) queryParams.append('offset', offset);
        queryParams.append('withTotal', withTotal);
        const queryString = queryParams.toString();

        try {
          const result = await baseQuery({
            url: `/${tenantId}/locations${queryString ? `?${queryString}` : ''}`,
            method: 'GET',
          });

          if (result.error) {
            return { error: result.error };
          }

          // Transform the response data here
          log.debug({
            message_type: 'Location',
            message: 'getLocations: transforming response',
          });

          const response = result.data;
          const results = response?.results;

          if (!results || !Array.isArray(results)) {
            log.warn({
              message_type: 'Location',
              message:
                'getLocations: no results found or results is not an array',
              data: response,
            });
            return { data: { results: [], limit: 0, offset: 0, count: 0, total: 0 } };
          }

          // Check if data is already in the correct format (has 'key' instead of 'location_id')
          const needsTransformation = results.length > 0 && results[0].location_id;

          const transformedData = needsTransformation
            ? results.map((location) => mapLocationToLocationRequest(location))
            : results;

          // Return paginated response with metadata
          return {
            data: {
              results: transformedData,
              limit: response.limit,
              offset: response.offset,
              count: response.count,
              total: response.total,
            }
          };
        } catch (error) {
          log.error({
            message_type: 'SystemError',
            message: 'getLocations: queryFn failure',
            meta_data: getStackTrace(),
            error,
          });
          return { error: { status: 500, data: error.message } };
        }
      },
      providesTags: ['Location'],
      async onQueryStarted(arg, { queryFulfilled }) {
        log.debug({
          message_type: 'Location',
          message: 'getLocations: pending',
        });
        try {
          const result = await queryFulfilled;
          log.info({
            message_type: 'Location',
            message: 'getLocations: fulfilled',
            data: result.data,
          });
        } catch (error) {
          log.error({
            message_type: 'SystemError',
            message: 'getLocations: failure',
            meta_data: getStackTrace(),
            error,
          });
        }
      },
    }),
    createLocation: builder.mutation({
      queryFn: async (location, { getState }, extraOptions, baseQuery) => {
        const state = getState();
        const tenantId =
          state.session.tenantID || state.tenant?.tenant?.tenant_id;

        if (!tenantId) {
          return {
            error: { status: 400, data: 'Tenant ID not found in state' },
          };
        }

        try {
          const result = await baseQuery({
            url: `/${tenantId}/locations`,
            method: 'POST',
            body: mapLocationToLocationRequest(location),
            headers: {
              'Content-Type': 'application/json',
            },
          });
          if (result.error) {
            return { error: result.error };
          }
          return { data: result.data };
        } catch (error) {
          log.error({
            message_type: 'SystemError',
            message: 'createLocation: failure',
            meta_data: getStackTrace(),
            error,
          });
          return { error: { status: 500, data: error.message } };
        }
      },
      invalidatesTags: ['Location'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        log.debug({
          message_type: 'Location',
          message: 'createLocation: pending',
        });

        // Manually update the cache for getLocations
        const postResult = dispatch(
          locationApi.util.updateQueryData(
            'getLocations',
            undefined,
            (draft) => {
              if (draft && draft.results && Array.isArray(draft.results)) {
                draft.results.push(arg);
                draft.total = (draft.total || 0) + 1;
              }
            }
          )
        );

        try {
          const result = await queryFulfilled;
          log.info({
            message_type: 'Location',
            message: 'createLocation: fulfilled',
            data: result.data,
          });
        } catch (error) {
          log.error({
            message_type: 'SystemError',
            message: 'createLocation: failure',
            meta_data: getStackTrace(),
            error,
          });
          postResult.undo(); // Undo the optimistic update on error
        }
      },
    }),
    updateLocation: builder.mutation({
      queryFn: async (location, { getState }, extraOptions, baseQuery) => {
        const state = getState();
        const tenantId =
          state.session.tenantID || state.tenant?.tenant?.tenant_id;

        if (!tenantId) {
          return {
            error: { status: 400, data: 'Tenant ID not found in state' },
          };
        }

        try {
          const result = await baseQuery({
            url: `/${tenantId}/locations/key=${location.location_id}`,
            method: 'PUT',
            body: mapLocationToLocationRequest(location),
            headers: {
              'Content-Type': 'application/json',
            },
          });
          if (result.error) {
            return { error: result.error };
          }
          return { data: result.data };
        } catch (error) {
          log.error({
            message_type: 'SystemError',
            message: 'updateLocation: failure',
            meta_data: getStackTrace(),
            error,
          });
          return { error: { status: 500, data: error.message } };
        }
      },
      invalidatesTags: ['Location'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        log.debug({
          message_type: 'Location',
          message: 'updateLocation: pending',
        });

        // Manually update the cache for getLocations
        const postResult = dispatch(
          locationApi.util.updateQueryData(
            'getLocations',
            undefined,
            (draft) => {
              if (draft && draft.results && Array.isArray(draft.results)) {
                const index = draft.results.findIndex((loc) => loc.id === arg.id);
                if (index !== -1) {
                  draft.results[index] = arg; // Update the existing location
                }
              }
            }
          )
        );

        try {
          const result = await queryFulfilled;
          log.info({
            message_type: 'Location',
            message: 'updateLocation: fulfilled',
            data: result.data,
          });
        } catch (error) {
          log.error({
            message_type: 'SystemError',
            message: 'updateLocation: failure',
            meta_data: getStackTrace(),
            error,
          });
          postResult.undo(); // Undo the optimistic update on error
        }
      },
    }),
    getCashdrawers: builder.query({
      query: (locationId) => ({
        url: `cashDrawers/${locationId}`,
        method: 'GET',
      }),
      providesTags: (result, error, locationId) => [
        { type: 'Location', id: locationId },
        'Cashdrawer',
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        log.debug({
          message_type: 'Location',
          message: 'getCashdrawers: pending',
        });
        try {
          const result = await queryFulfilled;
          log.info({
            message_type: 'Location',
            message: 'getCashdrawers: fulfilled',
            data: result.data,
          });
        } catch (error) {
          log.error({
            message_type: 'SystemError',
            message: 'getCashdrawers: failure',
            meta_data: getStackTrace(),
            error,
          });
        }
      },
    }),
    getWorkstations: builder.query({
      query: (locationId) => ({
        url: `workstations/${locationId}`,
        method: 'GET',
      }),
      providesTags: (result, error, locationId) => [
        { type: 'Location', id: locationId },
        'Workstation',
      ],

      async onQueryStarted(arg, { queryFulfilled }) {
        log.debug({
          message_type: 'Location',
          message: 'getWorkstations: pending',
        });
        try {
          const result = await queryFulfilled;
          log.info({
            message_type: 'Location',
            message: 'getWorkstations: fulfilled',
            data: result.data,
          });
        } catch (error) {
          log.error({
            message_type: 'SystemError',
            message: 'getWorkstations: failure',
            meta_data: getStackTrace(),
            error,
          });
        }
      },
    }),
    getDeviceDictionary: builder.query({
      query: () => ({
        url: 'deviceDictionary',
        method: 'GET',
      }),
      providesTags: ['Location', 'DeviceDictionary'],
      async onQueryStarted(arg, { queryFulfilled }) {
        log.debug({
          message_type: 'Location',
          message: 'getDeviceDictionary: pending',
        });
        try {
          const result = await queryFulfilled;
          log.info({
            message_type: 'Location',
            message: 'getDeviceDictionary: fulfilled',
            data: result.data,
          });
        } catch (error) {
          log.error({
            message_type: 'SystemError',
            message: 'getDeviceDictionary: failure',
            meta_data: getStackTrace(),
            error,
          });
        }
      },
    }),
    searchDevices: builder.query({
      query: (criteria) => {
        const queryString = Object.keys(criteria)
          .map((key) => `${key}=${criteria[key]}`)
          .join('&');
        return {
          url: `devices?${queryString}`,
          method: 'GET',
        };
      },
      providesTags: ['Location', 'Device'],
      async onQueryStarted(arg, { queryFulfilled }) {
        log.debug({
          message_type: 'Location',
          message: 'searchDevices: pending',
        });
        try {
          const result = await queryFulfilled;
          log.info({
            message_type: 'Location',
            message: 'searchDevices: fulfilled',
            data: result.data,
          });
        } catch (error) {
          log.error({
            message_type: 'SystemError',
            message: 'searchDevices: failure',
            meta_data: getStackTrace(),
            error,
          });
        }
      },
      transformResponse: (response) => {
        log.debug({
          message_type: 'Location',
          message: 'searchDevices: transformResponse',
          data: response,
        });
        return { metadata: response[0].metadata[0], data: response[0].data };
      },
    }),
    getDevice: builder.query({
      query: (deviceId) => ({
        url: `device/${deviceId}`,
        method: 'GET',
      }),
      providesTags: (result, error, { deviceId }) => [
        'Location',
        { type: 'Device', id: deviceId },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        log.debug({
          message_type: 'Location',
          message: 'getDevice: pending',
        });
        try {
          const result = await queryFulfilled;
          log.info({
            message_type: 'Location',
            message: 'getDevice: fulfilled',
            data: result.data,
          });
        } catch (error) {
          log.error({
            message_type: 'SystemError',
            message: 'getDevice: failure',
            meta_data: getStackTrace(),
            error,
          });
        }
      },
    }),
    deleteDevice: builder.mutation({
      query: (deviceId) => ({
        url: `device/${deviceId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { deviceId }) => [
        'Location',
        { type: 'Device', id: deviceId },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        log.debug({
          message_type: 'Location',
          message: 'deleteDevice: pending',
        });

        // Manually update the cache for searchDevices
        const postResult = dispatch(
          locationApi.util.updateQueryData('searchDevices', {}, (draft) => {
            if (draft && draft.data) {
              draft.data = draft.data.filter(
                (device) => device.id !== arg.deviceId
              );
            }
          })
        );

        try {
          const result = await queryFulfilled;
          log.info({
            message_type: 'Location',
            message: 'deleteDevice: fulfilled',
            data: result.data,
          });
        } catch (error) {
          log.error({
            message_type: 'SystemError',
            message: 'deleteDevice: failure',
            meta_data: getStackTrace(),
            error,
          });
          postResult.undo(); // Undo the optimistic update on error
        }
      },
    }),
    searchLocations: builder.query({
      queryFn: async (criteria, { getState }, extraOptions, baseQuery) => {
        const state = getState();
        const tenantId =
          state.session.tenantID || state.tenant?.tenant?.tenant_id;

        if (!tenantId) {
          return {
            error: { status: 400, data: 'Tenant ID not found in state' },
          };
        }

        try {
          // Convert page/rowsPerPage to limit/offset for the API
          const { page, rowsPerPage, ...otherCriteria } = criteria;
          const limit = rowsPerPage || 10;
          const offset = page ? (page - 1) * limit : 0;

          // Build query params
          const queryParams = new URLSearchParams();
          queryParams.append('limit', limit);
          queryParams.append('offset', offset);
          queryParams.append('withTotal', 'true');

          // Add any other search criteria
          Object.keys(otherCriteria).forEach((key) => {
            if (otherCriteria[key] !== undefined && otherCriteria[key] !== '') {
              queryParams.append(key, otherCriteria[key]);
            }
          });

          const result = await baseQuery({
            url: `/${tenantId}/locations?${queryParams.toString()}`,
            method: 'GET',
          });

          if (result.error) {
            return { error: result.error };
          }

          const response = result.data;
          const results = response?.results;

          if (!results || !Array.isArray(results)) {
            log.warn({
              message_type: 'Location',
              message:
                'searchLocations: no results found or results is not an array',
              data: response,
            });
            return { data: { results: [], limit: 0, offset: 0, count: 0, total: 0 } };
          }

          // Check if data needs transformation
          const needsTransformation = results.length > 0 && results[0].location_id !== undefined;

          const transformedData = needsTransformation
            ? results.map((location) => mapLocationToLocationRequest(location))
            : results;

          // Return paginated response with metadata
          return {
            data: {
              results: transformedData,
              limit: response.limit,
              offset: response.offset,
              count: response.count,
              total: response.total,
            }
          };
        } catch (error) {
          log.error({
            message_type: 'SystemError',
            message: 'searchLocations: queryFn failure',
            meta_data: getStackTrace(),
            error,
          });
          return { error: { status: 500, data: error.message } };
        }
      },
      providesTags: ['Location'],
      async onQueryStarted(arg, { queryFulfilled }) {
        log.debug({
          message_type: 'Location',
          message: 'searchLocations: pending',
        });
        try {
          const result = await queryFulfilled;
          log.info({
            message_type: 'Location',
            message: 'searchLocations: fulfilled',
            data: result.data,
          });
        } catch (error) {
          log.error({
            message_type: 'SystemError',
            message: 'searchLocations: failure',
            meta_data: getStackTrace(),
            error,
          });
        }
      },
    }),
  }),
});

export const {
  useGetLocationsQuery,
  useLazyGetLocationsQuery,
  useCreateLocationMutation,
  useUpdateLocationMutation,
  useGetCashdrawersQuery,
  useLazyGetCashdrawersQuery,
  useGetWorkstationsQuery,
  useLazyGetWorkstationsQuery,
  useGetDeviceDictionaryQuery,
  useLazyGetDeviceDictionaryQuery,
  useSearchDevicesQuery,
  useLazySearchDevicesQuery,
  useLazyGetDeviceQuery,
  useDeleteDeviceMutation,
  useSearchLocationsQuery,
  useLazySearchLocationsQuery,
} = locationApi;
